import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import type { Product, StockMovement, StockMovementType } from '../types'

export interface StockMovementWithProduct extends StockMovement {
  products: { sku: string; name: string } | null
}

export interface CreateMovementInput {
  product_id: string
  tipo: StockMovementType
  quantidade: number
  valor_unitario: number
  numero_nota?: string | null
  serie_nota?: string | null
  chave_nfe?: string | null
  supplier_id?: string | null
  fornecedor_nome?: string | null
  fornecedor_cnpj?: string | null
  observacao?: string | null
}

export function useStockMovements(initialLimit = 20) {
  const [movements, setMovements] = useState<StockMovementWithProduct[]>([])
  const [companyId, setCompanyId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchRecent = useCallback(async (limit = initialLimit) => {
    const { data, error: err } = await supabase
      .from('stock_movements')
      .select('*, products(sku, name)')
      .order('created_at', { ascending: false })
      .limit(limit)
    if (err) { setError(err.message); return }
    setMovements((data ?? []) as unknown as StockMovementWithProduct[])
  }, [initialLimit])

  const loadCompany = useCallback(async () => {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return null
    const { data: profile } = await supabase
      .from('profiles').select('company_id').eq('id', user.id).single()
    const id = (profile?.company_id as string | undefined) ?? null
    setCompanyId(id)
    return id
  }, [])

  useEffect(() => {
    (async () => {
      setLoading(true)
      try {
        await loadCompany()
        await fetchRecent()
      } catch (e) {
        setError((e as Error).message)
      } finally {
        setLoading(false)
      }
    })()
  }, [loadCompany, fetchRecent])

  /**
   * Busca produto pelo codigo bipado. Procura SOMENTE em products.barcode
   * (GTIN/EAN). SKU nao e usado como fallback porque sao identificadores
   * diferentes (SKU = codigo interno do ERP; GTIN = padrao global de
   * barras). Inclui inativos para permitir reativacao.
   */
  const findProductByBarcode = useCallback(async (code: string): Promise<Product | null> => {
    const c = code.trim()
    if (!c) return null
    const { data, error: err } = await supabase
      .from('products').select('*').eq('barcode', c).is('deleted_at', null).maybeSingle()
    if (err) { setError(err.message); return null }
    return (data as Product | null) ?? null
  }, [])

  /**
   * Busca produtos por SKU ou trecho do nome (usado na aba Cadastro para
   * o usuario achar produto vindo do Tiny e preencher o codigo de barras
   * que esta vazio). Retorna ate 20 resultados, inclui inativos.
   */
  const searchProducts = useCallback(async (query: string): Promise<Product[]> => {
    const q = query.trim()
    if (!q) return []
    const { data, error: err } = await supabase
      .from('products')
      .select('*')
      .or(`sku.ilike.%${q}%,name.ilike.%${q}%`)
      .is('deleted_at', null)
      .order('is_active', { ascending: false })
      .limit(20)
    if (err) { setError(err.message); return [] }
    return (data ?? []) as Product[]
  }, [])

  /** Associa um codigo de barras a um SKU existente (one-time enrichment). */
  const associateBarcode = useCallback(async (productId: string, barcode: string): Promise<{ ok: boolean; error?: string }> => {
    const { error: err } = await supabase
      .from('products').update({ barcode: barcode.trim() }).eq('id', productId)
    if (err) return { ok: false, error: err.message }
    return { ok: true }
  }, [])

  /** Reativa um produto inativo (is_active = true). */
  const reactivateProduct = useCallback(async (productId: string): Promise<{ ok: boolean; error?: string }> => {
    const { error: err } = await supabase
      .from('products').update({ is_active: true }).eq('id', productId)
    if (err) return { ok: false, error: err.message }
    return { ok: true }
  }, [])

  const createMovement = useCallback(async (input: CreateMovementInput): Promise<{ ok: boolean; error?: string }> => {
    let cId = companyId
    if (!cId) cId = await loadCompany()
    if (!cId) return { ok: false, error: 'Empresa nao carregada' }
    const { data: { user } } = await supabase.auth.getUser()
    const { error: err } = await supabase.from('stock_movements').insert({
      company_id: cId,
      product_id: input.product_id,
      tipo: input.tipo,
      quantidade: input.quantidade,
      valor_unitario: input.valor_unitario,
      numero_nota: input.numero_nota ?? null,
      serie_nota: input.serie_nota ?? null,
      chave_nfe: input.chave_nfe ?? null,
      supplier_id: input.supplier_id ?? null,
      fornecedor_nome: input.fornecedor_nome ?? null,
      fornecedor_cnpj: input.fornecedor_cnpj ?? null,
      observacao: input.observacao ?? null,
      created_by: user?.id ?? null,
    })
    if (err) return { ok: false, error: err.message }
    await fetchRecent()
    return { ok: true }
  }, [companyId, loadCompany, fetchRecent])

  return {
    movements,
    companyId,
    loading,
    error,
    fetchRecent,
    createMovement,
    findProductByBarcode,
    searchProducts,
    associateBarcode,
    reactivateProduct,
  }
}
